import { useEffect, useMemo, useState } from 'react';
import {
  ChevronDown,
  ChevronUp,
  ListPlus,
  Plus,
  Trash2,
  Type,
  X,
} from 'lucide-react';
import clsx from 'clsx';
import type { LogLine } from '@/lib/logcat/parser';
import type {
  KeywordRule,
  Rule,
  SequenceRule,
  SequenceStep,
  Suite,
  SuiteListItem,
} from '@/lib/checkpoint/types';
import { checkpointApi } from '@/lib/checkpoint/api';
import { stringify as yamlStringify } from 'yaml';

type LevelChar = 'V' | 'D' | 'I' | 'W' | 'E' | 'F' | 'A';
const ALL_LEVEL_CHARS: LevelChar[] = ['V', 'D', 'I', 'W', 'E', 'F', 'A'];

type Severity = 'pass' | 'mid' | 'fail';

/**
 * Modal 触发 payload —— Logs.tsx 根据用户操作选择不同 mode 调起。
 *
 *   - single-line：右键单行（保留旧行为）
 *   - selection：选区片段，文本可能短于一整行；统一走 keyword 三段式
 *   - multi-line-keywords：多行勾选，每行做一条 keyword 规则
 *   - multi-line-sequence：多行勾选 / 跨行选区，按行号升序构造 sequence
 *
 * 第一版引擎已下线 regex matcher，相应的 selection.matcher 字段一并删除。
 */
export type LineToRulePayload =
  | { mode: 'single-line'; line: LogLine }
  | { mode: 'selection'; line: LogLine; text: string }
  | { mode: 'multi-line-keywords'; lines: LogLine[] }
  | { mode: 'multi-line-sequence'; lines: LogLine[] };

interface Props {
  payload: LineToRulePayload;
  /** 当前 logcat tab 的 App 包名（用于"复制成新套件"时填 appPackage）。 */
  currentPackage?: string;
  onClose: () => void;
  onSaved: (suiteId: string) => void;
}

/* -------------------------------------------------------------------- */
/* Helpers: payload → 草稿规则                                          */
/* -------------------------------------------------------------------- */

function severityFromLevel(lv: string): Severity {
  if (lv === 'E' || lv === 'F') return 'fail';
  if (lv === 'W') return 'mid';
  return 'pass';
}

function buildKeywordRule(
  line: LogLine,
  text: string,
  severity: Severity,
  ruleId: string,
): KeywordRule {
  return {
    id: ruleId,
    name: `${line.tag || 'rule'}: ${text.slice(0, 60)}`,
    severity,
    matcher: 'keyword',
    keywords: [text],
    ...(line.tag ? { tags: [line.tag] } : {}),
    ...(ALL_LEVEL_CHARS.includes(line.level as LevelChar)
      ? { levels: [line.level as LevelChar] }
      : {}),
  };
}

function buildSequenceRule(lines: LogLine[], ruleId: string): SequenceRule {
  const steps: SequenceStep[] = lines.map((line, i) => ({
    name: `step ${i + 1}: ${line.tag || ''}`.trim(),
    keywords: [line.message.slice(0, 200) || line.raw.slice(0, 200)],
    ...(line.tag ? { tags: [line.tag] } : {}),
  }));
  return {
    id: ruleId,
    name: `sequence: ${lines.length} 步`,
    severity: 'pass',
    matcher: 'sequence',
    steps,
  };
}

/** 根据 payload 推导初始 draft 规则数组 + 默认 mode/matcher。 */
function payloadToInitialDrafts(payload: LineToRulePayload): {
  drafts: Rule[];
  defaultName: string;
  initialPackage: string | undefined;
} {
  const stamp = (i = 0): string => `r-${Date.now().toString(36)}-${i}`;
  switch (payload.mode) {
    case 'single-line': {
      const line = payload.line;
      const text = line.message || line.raw;
      return {
        drafts: [
          buildKeywordRule(line, text.slice(0, 200), severityFromLevel(line.level), stamp(0)),
        ],
        defaultName: `行规则 · ${line.tag || '无 tag'}`,
        initialPackage: undefined,
      };
    }
    case 'selection': {
      const line = payload.line;
      const sev = severityFromLevel(line.level);
      const rule = buildKeywordRule(line, payload.text, sev, stamp(0));
      return {
        drafts: [rule],
        defaultName: `选区规则 · ${payload.text.slice(0, 32)}`,
        initialPackage: undefined,
      };
    }
    case 'multi-line-keywords': {
      const drafts: Rule[] = payload.lines.map((line, i) =>
        buildKeywordRule(
          line,
          (line.message || line.raw).slice(0, 200),
          severityFromLevel(line.level),
          stamp(i),
        ),
      );
      return {
        drafts,
        defaultName: `多行关键字 · ${payload.lines.length} 条`,
        initialPackage: undefined,
      };
    }
    case 'multi-line-sequence': {
      const sorted = [...payload.lines].sort((a, b) => (a.id ?? 0) - (b.id ?? 0));
      return {
        drafts: [buildSequenceRule(sorted, stamp(0))],
        defaultName: `序列规则 · ${sorted.length} 步`,
        initialPackage: undefined,
      };
    }
  }
}

/* -------------------------------------------------------------------- */
/* Component                                                            */
/* -------------------------------------------------------------------- */

export function LineToRuleModal({
  payload,
  currentPackage,
  onClose,
  onSaved,
}: Props): JSX.Element {
  const initial = useMemo(() => payloadToInitialDrafts(payload), [payload]);
  const [drafts, setDrafts] = useState<Rule[]>(initial.drafts);
  const [items, setItems] = useState<SuiteListItem[]>([]);
  const [target, setTarget] = useState<'append' | 'create'>('append');
  const [pickedSuite, setPickedSuite] = useState<string>('');
  const [newName, setNewName] = useState<string>(initial.defaultName);
  const [newId, setNewId] = useState<string>(`line-rules-${Date.now().toString(36)}`);
  const [appPackage, setAppPackage] = useState<string>(currentPackage ?? '*');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    checkpointApi
      .listSuites()
      .then((r) => {
        setItems(r.suites);
        if (r.suites.length === 0) setTarget('create');
        else setPickedSuite(r.suites[0].id);
      })
      .catch((e) => setError(e instanceof Error ? e.message : String(e)));
  }, []);

  const updateDraft = (id: string, patch: Partial<Rule>): void => {
    setDrafts((prev) =>
      prev.map((r) => (r.id === id ? ({ ...r, ...patch } as Rule) : r)),
    );
  };

  const removeDraft = (id: string): void => {
    setDrafts((prev) => prev.filter((r) => r.id !== id));
  };

  const moveDraft = (id: string, dir: -1 | 1): void => {
    setDrafts((prev) => {
      const idx = prev.findIndex((r) => r.id === id);
      if (idx < 0) return prev;
      const next = idx + dir;
      if (next < 0 || next >= prev.length) return prev;
      const out = prev.slice();
      [out[idx], out[next]] = [out[next], out[idx]];
      return out;
    });
  };

  const addBlankKeyword = (): void => {
    const r: KeywordRule = {
      id: `r-${Date.now().toString(36)}-${drafts.length}`,
      name: '新规则',
      severity: 'pass',
      matcher: 'keyword',
      keywords: [''],
    };
    setDrafts((prev) => [...prev, r]);
  };

  const handleSave = async (): Promise<void> => {
    if (drafts.length === 0) {
      setError('草稿清单为空');
      return;
    }
    // 简单校验
    for (const r of drafts) {
      if (r.matcher === 'keyword' && (!r.keywords || r.keywords.every((k) => !k.trim()))) {
        setError(`规则 ${r.id} 的关键字为空`);
        return;
      }
    }
    setBusy(true);
    setError(null);
    try {
      let suiteId: string;
      let yaml: string;
      if (target === 'append' && pickedSuite) {
        const detail = await checkpointApi.getSuite(pickedSuite);
        const suite: Suite = {
          ...detail.suite,
          rules: [...detail.suite.rules, ...drafts],
          updatedAt: new Date().toISOString(),
        };
        yaml = yamlStringify(suite);
        suiteId = suite.id;
        await checkpointApi.updateSuite(suiteId, yaml);
      } else {
        const suite: Suite = {
          schemaVersion: 1,
          id: newId,
          name: newName,
          description: `由 LineToRuleModal 生成（${drafts.length} 条规则）`,
          appPackage: appPackage || '*',
          createdAt: new Date().toISOString(),
          parameters: [],
          rules: drafts,
        };
        yaml = yamlStringify(suite);
        suiteId = newId;
        await checkpointApi.saveSuite(yaml);
      }
      onSaved(suiteId);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const previewYaml = useMemo(() => {
    const suite: Suite = {
      schemaVersion: 1,
      id: target === 'create' ? newId : pickedSuite || '<append>',
      name: target === 'create' ? newName : '<追加到已有套件>',
      appPackage: appPackage || '*',
      parameters: [],
      rules: drafts,
    };
    try {
      return yamlStringify(suite);
    } catch {
      return '<yaml 渲染失败>';
    }
  }, [drafts, target, newId, newName, pickedSuite, appPackage]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="grid h-[80vh] w-[920px] max-w-full grid-cols-[minmax(0,1fr)_360px] overflow-hidden rounded-lg border border-line bg-bg-card shadow-2xl">
        {/* 左侧：草稿清单编辑器 */}
        <div className="flex h-full min-w-0 flex-col">
          <header className="flex items-center justify-between border-b border-line px-3 py-2">
            <div>
              <h2 className="text-sm font-semibold">把日志内容做成检查规则</h2>
              <p className="text-[11px] text-ink-muted">
                来源：{describePayload(payload)} · 草稿 {drafts.length} 条
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-ink-muted hover:text-ink"
              disabled={busy}
              title="关闭"
            >
              <X className="h-4 w-4" />
            </button>
          </header>
          <div className="flex-1 overflow-y-auto p-3">
            <ul className="space-y-2">
              {drafts.map((r, i) => (
                <li key={r.id}>
                  <DraftCard
                    rule={r}
                    onChange={(patch) => updateDraft(r.id, patch)}
                    onRemove={() => removeDraft(r.id)}
                    onMoveUp={i > 0 ? () => moveDraft(r.id, -1) : undefined}
                    onMoveDown={i < drafts.length - 1 ? () => moveDraft(r.id, 1) : undefined}
                  />
                </li>
              ))}
              {drafts.length === 0 && (
                <li className="rounded-md border border-dashed border-line p-3 text-center text-[11px] text-ink-muted">
                  草稿清单为空 —— 选区或多选后再回来填。
                </li>
              )}
            </ul>
            <button
              type="button"
              className="mt-3 inline-flex items-center gap-1 rounded-md border border-dashed border-line px-2 py-1 text-[11px] text-ink-muted hover:bg-bg-soft hover:text-ink"
              onClick={addBlankKeyword}
            >
              <Plus className="h-3 w-3" /> 追加空白关键字规则
            </button>
          </div>
        </div>

        {/* 右侧：保存目标 + YAML 预览 + 错误 */}
        <aside className="flex h-full flex-col border-l border-line bg-bg-soft">
          <header className="border-b border-line px-3 py-2">
            <h3 className="text-xs font-semibold">保存到</h3>
          </header>
          <div className="space-y-2 p-3 text-[11px]">
            <label className="flex items-start gap-1.5">
              <input
                type="radio"
                name="target"
                checked={target === 'append'}
                onChange={() => setTarget('append')}
                disabled={items.length === 0}
                className="mt-0.5"
              />
              <span className="flex-1">
                追加到已有套件
                <select
                  className="select mt-1 w-full py-0.5"
                  value={pickedSuite}
                  disabled={target !== 'append'}
                  onChange={(e) => setPickedSuite(e.target.value)}
                >
                  {items.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}（{s.id}）
                    </option>
                  ))}
                </select>
              </span>
            </label>
            <label className="flex items-start gap-1.5">
              <input
                type="radio"
                name="target"
                checked={target === 'create'}
                onChange={() => setTarget('create')}
                className="mt-0.5"
              />
              <span className="flex-1">
                复制成新套件
                <input
                  className="input mt-1 w-full font-mono"
                  placeholder="套件 id"
                  value={newId}
                  disabled={target !== 'create'}
                  onChange={(e) => setNewId(e.target.value)}
                />
                <input
                  className="input mt-1 w-full"
                  placeholder="套件名称"
                  value={newName}
                  disabled={target !== 'create'}
                  onChange={(e) => setNewName(e.target.value)}
                />
                <input
                  className="input mt-1 w-full font-mono"
                  placeholder="appPackage（默认 *）"
                  value={appPackage}
                  disabled={target !== 'create'}
                  onChange={(e) => setAppPackage(e.target.value)}
                />
              </span>
            </label>
          </div>
          <div className="border-t border-line px-3 py-1 text-[11px] text-ink-muted">YAML 预览</div>
          <pre className="flex-1 overflow-auto bg-bg-card px-3 py-2 font-mono text-[10px] leading-snug text-ink">
            {previewYaml}
          </pre>
          {error && (
            <div className="border-t border-rose-500/30 bg-rose-500/10 px-3 py-2 text-[11px] text-rose-300">
              {error}
            </div>
          )}
          <footer className="flex items-center justify-end gap-2 border-t border-line px-3 py-2">
            <button className="btn" onClick={onClose} disabled={busy}>
              取消
            </button>
            <button
              className="btn-primary"
              onClick={() => void handleSave()}
              disabled={busy || drafts.length === 0}
            >
              {busy ? '保存中…' : `保存 ${drafts.length} 条规则`}
            </button>
          </footer>
        </aside>
      </div>
    </div>
  );
}

function describePayload(p: LineToRulePayload): string {
  switch (p.mode) {
    case 'single-line':
      return '单行（右键）';
    case 'selection':
      return `选区 · keyword`;
    case 'multi-line-keywords':
      return `多行关键字 · ${p.lines.length} 行`;
    case 'multi-line-sequence':
      return `多行序列 · ${p.lines.length} 步`;
  }
}

/* -------------------------------------------------------------------- */
/* Draft card                                                           */
/* -------------------------------------------------------------------- */

interface DraftProps {
  rule: Rule;
  onChange: (patch: Partial<Rule>) => void;
  onRemove: () => void;
  onMoveUp?: () => void;
  onMoveDown?: () => void;
}

function DraftCard({ rule, onChange, onRemove, onMoveUp, onMoveDown }: DraftProps): JSX.Element {
  return (
    <div className="rounded-md border border-line bg-bg-card p-2">
      <div className="flex items-center gap-1.5">
        <MatcherBadge matcher={rule.matcher} />
        <input
          className="input flex-1 py-0.5 font-mono text-[12px]"
          value={rule.name}
          onChange={(e) => onChange({ name: e.target.value })}
        />
        <select
          className="select py-0.5 text-[11px]"
          value={rule.severity}
          onChange={(e) => onChange({ severity: e.target.value as Severity })}
          title="严重等级"
        >
          <option value="pass">pass</option>
          <option value="mid">mid</option>
          <option value="fail">fail</option>
        </select>
        <div className="flex items-center gap-0.5">
          {onMoveUp && (
            <button
              className="btn-ghost px-1 py-0.5"
              onClick={onMoveUp}
              title="上移"
            >
              <ChevronUp className="h-3 w-3" />
            </button>
          )}
          {onMoveDown && (
            <button
              className="btn-ghost px-1 py-0.5"
              onClick={onMoveDown}
              title="下移"
            >
              <ChevronDown className="h-3 w-3" />
            </button>
          )}
          <button
            className="btn-ghost px-1 py-0.5 text-rose-400 hover:bg-rose-500/10"
            onClick={onRemove}
            title="删除"
          >
            <Trash2 className="h-3 w-3" />
          </button>
        </div>
      </div>
      <div className="mt-1.5 space-y-1.5 text-[11px]">
        {rule.matcher === 'keyword' && (
          <KeywordEditor
            rule={rule}
            onChange={(patch) => onChange(patch as Partial<Rule>)}
          />
        )}
        {rule.matcher === 'sequence' && (
          <SequenceEditor
            rule={rule}
            onChange={(patch) => onChange(patch as Partial<Rule>)}
          />
        )}
      </div>
    </div>
  );
}

function MatcherBadge({ matcher }: { matcher: Rule['matcher'] }): JSX.Element {
  const { Icon, label, cls } = matcherMeta(matcher);
  return (
    <span
      className={clsx(
        'inline-flex items-center gap-0.5 rounded px-1 py-0.5 text-[10px] font-medium',
        cls,
      )}
    >
      <Icon className="h-3 w-3" />
      {label}
    </span>
  );
}

function matcherMeta(matcher: Rule['matcher']): {
  Icon: typeof Type;
  label: string;
  cls: string;
} {
  switch (matcher) {
    case 'keyword':
      return { Icon: Type, label: 'kw', cls: 'bg-emerald-500/20 text-emerald-300' };
    case 'sequence':
      return { Icon: ListPlus, label: 'seq', cls: 'bg-amber-500/20 text-amber-300' };
  }
}

function tokenCount(s: string): number {
  const t = s.replace(/^\s+|\s+$/g, '');
  if (t === '') return 0;
  return t.split(/\s+/).length;
}

function KeywordEditor({
  rule,
  onChange,
}: {
  rule: KeywordRule;
  onChange: (patch: Partial<KeywordRule>) => void;
}): JSX.Element {
  const keywords = rule.keywords ?? [];
  return (
    <div className="space-y-1">
      <textarea
        className="input min-h-[40px] w-full font-mono text-[11px]"
        placeholder="每行一个关键字（任一命中即视为命中）"
        value={keywords.join('\n')}
        onChange={(e) =>
          onChange({
            keywords: e.target.value.split('\n'),
          })
        }
      />
      {/*
        token 数提示：≥2 token 提示可参与骨架对齐；=1 token 提示只能字面 +
        启发式（数字 / hex / quoted / activity / 包名）。空行忽略。
      */}
      <ul className="space-y-0.5 text-[10px] text-ink-muted">
        {keywords.map((k, i) => {
          const n = tokenCount(k);
          if (n === 0) return null;
          return (
            <li key={i} className="font-mono">
              <span className="text-ink-dim">#{i + 1}</span>{' '}
              {n >= 2 ? (
                <span className="text-emerald-300">
                  {n} 个 token，可参与骨架匹配
                </span>
              ) : (
                <span className="text-amber-300">
                  单 token，仅字面 + 启发式（含数字/hex/包名等才生效）
                </span>
              )}
            </li>
          );
        })}
      </ul>
      <label className="flex items-center gap-1.5 text-[10px] text-ink-muted">
        <input
          type="checkbox"
          checked={!!rule.strict}
          onChange={(e) => onChange({ strict: e.target.checked || undefined })}
        />
        <span>严格匹配（关闭骨架 / 启发式 fallback）</span>
      </label>
      <FilterTagLevelInputs
        tags={rule.tags}
        levels={rule.levels}
        onChange={(patch) => onChange(patch)}
      />
    </div>
  );
}

function SequenceEditor({
  rule,
  onChange,
}: {
  rule: SequenceRule;
  onChange: (patch: Partial<SequenceRule>) => void;
}): JSX.Element {
  const moveStep = (i: number, dir: -1 | 1): void => {
    const next = i + dir;
    if (next < 0 || next >= rule.steps.length) return;
    const out = rule.steps.slice();
    [out[i], out[next]] = [out[next], out[i]];
    onChange({ steps: out });
  };
  const updateStep = (i: number, patch: Partial<SequenceStep>): void => {
    const out = rule.steps.slice();
    out[i] = { ...out[i], ...patch };
    onChange({ steps: out });
  };
  const removeStep = (i: number): void => {
    onChange({ steps: rule.steps.filter((_, j) => j !== i) });
  };
  return (
    <div className="space-y-1">
      <ol className="space-y-1.5">
        {rule.steps.map((step, i) => (
          <li
            key={i}
            className="rounded-md border border-line bg-bg-soft p-1.5"
          >
            <div className="flex items-center gap-1">
              <span className="font-mono text-[10px] text-ink-muted">#{i + 1}</span>
              <input
                className="input flex-1 py-0.5 text-[11px]"
                placeholder="step 名"
                value={step.name ?? ''}
                onChange={(e) => updateStep(i, { name: e.target.value })}
              />
              <button
                className="btn-ghost px-1 py-0.5"
                onClick={() => moveStep(i, -1)}
                disabled={i === 0}
                title="上移"
              >
                <ChevronUp className="h-3 w-3" />
              </button>
              <button
                className="btn-ghost px-1 py-0.5"
                onClick={() => moveStep(i, 1)}
                disabled={i === rule.steps.length - 1}
                title="下移"
              >
                <ChevronDown className="h-3 w-3" />
              </button>
              <button
                className="btn-ghost px-1 py-0.5 text-rose-400"
                onClick={() => removeStep(i)}
                title="删除该 step"
              >
                <Trash2 className="h-3 w-3" />
              </button>
            </div>
            <textarea
              className="input mt-1 min-h-[34px] w-full font-mono text-[11px]"
              placeholder="step 的关键字（每行一个，任一命中即推进）"
              value={(step.keywords ?? []).join('\n')}
              onChange={(e) =>
                updateStep(i, { keywords: e.target.value.split('\n') })
              }
            />
          </li>
        ))}
      </ol>
      <button
        className="inline-flex items-center gap-1 rounded-md border border-dashed border-line px-2 py-0.5 text-[10px] text-ink-muted hover:bg-bg-soft hover:text-ink"
        onClick={() =>
          onChange({
            steps: [...rule.steps, { name: `step ${rule.steps.length + 1}`, keywords: [''] }],
          })
        }
      >
        <Plus className="h-3 w-3" /> 追加 step
      </button>
    </div>
  );
}

function FilterTagLevelInputs({
  tags,
  levels,
  onChange,
}: {
  tags?: string[];
  levels?: LevelChar[];
  onChange: (patch: { tags?: string[]; levels?: LevelChar[] }) => void;
}): JSX.Element {
  return (
    <div className="flex items-center gap-1.5">
      <label className="flex items-center gap-1 text-ink-muted">
        tag
        <input
          className="input py-0.5 font-mono text-[10px]"
          placeholder="多个用 , 分隔"
          value={(tags ?? []).join(',')}
          onChange={(e) =>
            onChange({
              tags: e.target.value
                .split(',')
                .map((t) => t.trim())
                .filter(Boolean),
            })
          }
        />
      </label>
      <label className="flex items-center gap-1 text-ink-muted">
        level
        <input
          className="input py-0.5 font-mono text-[10px]"
          placeholder="例如 EW"
          value={(levels ?? []).join('')}
          onChange={(e) => {
            const lvs = e.target.value
              .toUpperCase()
              .split('')
              .filter((c): c is LevelChar => ALL_LEVEL_CHARS.includes(c as LevelChar));
            onChange({
              levels: lvs.length > 0 ? lvs : undefined,
            });
          }}
        />
      </label>
    </div>
  );
}
